import { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import useCallApiAndLoad from '../../../hooks/useCallApiAndLoad';
import useAuth from '../../../hooks/useAuth';
import {
  towerLayoutV2Endpoint,
  saveTowerLayoutV2Endpoint,
  uploadSvgV2Endpoint,
} from '../../withdrawal/services/projectService';
import { TowerLayoutV2Detail, DistributionV2 } from '../models/towerLayoutModelV2';

export interface EditableFloorV2 {
  id: string;
  level: number;
  x: number;
  y: number;
  svgWidth: number;
  svgHeight: number;
  svgViewBox: string;
  path: string;
  fileName: string;
  status: string;
  svg_url: string;
  placed: boolean;
  isNew?: boolean;
  available_units?: number;
  total_units?: number;
  sold_units_percentage?: number;
  min_price?: number;
  avg_price_sqm?: number;
}

export interface PaletteSvgV2 {
  id: string;
  fileName: string;
  viewBox: string;
  width: number;
  height: number;
  pathD: string;
  svgUrl: string;
}

const useProjectTowerLayoutV2 = () => {
  const { id = '' } = useParams();
  const { callEndpoint, isLoading } = useCallApiAndLoad();
  const { getAuthToken } = useAuth();
  const token = getAuthToken();

  const [towerInfo, setTowerInfo] = useState<TowerLayoutV2Detail | null>(null);
  const [placedFloors, setPlacedFloors] = useState<EditableFloorV2[]>([]);
  const [svgPalette, setSvgPalette] = useState<PaletteSvgV2[]>([]);
  const [selectedFloorId, setSelectedFloorId] = useState<string | null>(null);

  const loadTowerLayout = useCallback(async () => {
    const { status, data: responseData } = await callEndpoint(
      towerLayoutV2Endpoint(id, token),
    );
    if (status === 200) {
      const details: TowerLayoutV2Detail = responseData?.data;
      setTowerInfo(details);
      if (details?.distribution) {
        const mappedFloors: EditableFloorV2[] = details.distribution.map((d) => ({
          id: d.id || crypto.randomUUID(),
          level: d.level,
          x: d.svg_layout?.x || 0,
          y: d.svg_layout?.y || 0,
          svgWidth: d.svg_layout?.svgWidth || 100,
          svgHeight: d.svg_layout?.svgHeight || 100,
          svgViewBox: d.svg_layout?.svgViewBox || '0 0 100 100',
          path: d.svg_layout?.path || '',
          fileName: d.svg_layout?.fileName || '',
          status: d.status,
          svg_url: d.svg_url,
          placed: true,
          isNew: false,
          available_units: d.available_units,
          total_units: d.total_units,
          sold_units_percentage: d.sold_units_percentage,
          min_price: d.min_price,
          avg_price_sqm: d.avg_price_sqm,
        }));
        setPlacedFloors(mappedFloors);
      }
    }
  }, [id, token]);

  const handleUploadSvgFile = async (file: File) => {
    // 1. Parse file in browser
    const text = await file.text();
    const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
    const svg = doc.querySelector('svg');
    const path = doc.querySelector('path');

    const viewBox = svg?.getAttribute('viewBox') || '0 0 100 100';
    const width = parseFloat(svg?.getAttribute('width') || '100');
    const height = parseFloat(svg?.getAttribute('height') || '100');
    const pathD = path?.getAttribute('d') || '';

    // 2. Upload file to GCS via backend
    const formData = new FormData();
    formData.append('file', file);

    const { status, data: responseData } = await callEndpoint(
      uploadSvgV2Endpoint(id, formData, token),
    );

    if (status === 200 || status === 201) {
      const gcsUrl = responseData?.data?.raw_svg_url || responseData?.data?.svg_url;
      const newPaletteSvg: PaletteSvgV2 = {
        id: crypto.randomUUID(),
        fileName: file.name,
        viewBox,
        width,
        height,
        pathD,
        svgUrl: gcsUrl,
      };
      setSvgPalette((prev) => [...prev, newPaletteSvg]);
      return newPaletteSvg;
    } else {
      alert('Error al subir el archivo SVG');
      return null;
    }
  };

  const handleSave = async (floorsToSave: EditableFloorV2[]): Promise<boolean> => {
    // Transform floors to matches payload format
    const payloadFloors = floorsToSave
      .filter((f) => f.svg_url && f.path)
      .map((f) => ({
        id: f.isNew ? undefined : f.id,
        level: f.level,
        status: f.status,
        svg_url: f.svg_url,
        svg_layout: {
          x: f.x,
          y: f.y,
          svgWidth: f.svgWidth,
          svgHeight: f.svgHeight,
          svgViewBox: f.svgViewBox,
          path: f.path,
          fileName: f.fileName,
        },
      }));

    const payload = {
      floors: payloadFloors,
    };

    const { status } = await callEndpoint(
      saveTowerLayoutV2Endpoint(id, payload, token),
    );

    if (status === 200 || status === 201) {
      loadTowerLayout();
      return true;
    } else {
      return false;
    }
  };

  return {
    towerInfo,
    placedFloors,
    setPlacedFloors,
    svgPalette,
    setSvgPalette,
    selectedFloorId,
    setSelectedFloorId,
    loadTowerLayout,
    handleUploadSvgFile,
    handleSave,
    isLoading,
  };
};

export default useProjectTowerLayoutV2;
